import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, Select, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LocationLockEnum, WLocation } from '@/types/inventoryV2';
import SText from '@/styles/SText';
import { useTranslation } from 'react-i18next';
import useLabelColor from '../InventoryManage/hooks/useLabelColor';
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from '@/styles/STableLayout';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { AnyObject } from 'antd/es/_util/type';
import useDataList from '@/hooks/useDataList';
import { CommonListParams } from '@/types/common';
import { locationList } from '@/apis/inventoryv2';
import LocationDetail from '../InventoryManage/components/LocationDetail';
import BarcodeList from '../InventoryManage/components/BarcodeList';
import LocationForm from '../InventoryManage/components/LocationForm';
import PrintLocation from './components/PrintLocation';
import LocationRefProducts from './components/LocationRefProducts';
import { getLocationCode } from '../../utils';
import { getLocationIdx } from '@/utils/wLocation';
import ExportExcelButton from '@/components/ExportExcelButton';
import ExportProgressModal from '@/components/ExportProgressModal';
import { DictContext } from '@/layout/context/DictContext';
import SelectLocationType from '@/components/Selector/SelectLocationType';
import ListFilterBar from '@/components/ListFilterBar';
import useCommittedFilters from '@/components/ListFilterBar/hooks/useCommittedFilters';

interface LocationFilters {
  keyword: string;
  locationType: number | undefined;
}

const INITIAL_LOCATION_FILTERS: LocationFilters = {
  keyword: '',
  locationType: undefined,
};

const LocationManage: React.FC<{ header?: ReactNode }> = ({ header }) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const { getLabelColor } = useLabelColor();
  const { locationTypes } = useContext(DictContext);
  const [lList, lTotal, lLoading, { fetchList: getLList, addItem: addLItem, updateItem: updateLItem }] = useDataList<WLocation, [CommonListParams, number | undefined]>({ fetchApi: locationList });

  const [lDetail, setLDetail] = useState<WLocation | null>(null);
  const [barcodeInfo, setBarcodeInfo] = useState<{ id: string; type: 'inventory' | 'location' } | null>(null);
  const [locationFormOpen, setLocationFormOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<WLocation | undefined>(undefined);
  const [exportProgressVisible, setExportProgressVisible] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [printLoading, setPrintLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<LocationFilters>(INITIAL_LOCATION_FILTERS);
  const { keyword, locationType } = draftFilters;

  // ✅ 取消请求：记录当前进行中的请求控制器
  const abortControllerRef = useRef<AbortController | null>(null);

  // 组件卸载时取消进行中的请求
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_LOCATION_FILTERS);
    fetch(0, nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceFilters])

  const LocationColums: ColumnsType<WLocation> = [
    {
      title: <SText text={t("warehouse.locationCode")} />,
      dataIndex: "number",
      width: 260,
      sorter: (a, b) => {
        const valA = getLocationCode(a) ?? "";
        const valB = getLocationCode(b) ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => <SText text={getLocationCode(record)} />
    },
    {
      title: t("warehouse.labels"),
      key: "labels",
      width: 420,
      render: (_, record) => (
        <Flex gap={4} wrap="wrap">
          {record.label1 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label1")} text={record.label1} />}
          {record.label2 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label2")} text={record.label2} />}
          {record.label3 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label3")} text={record.label3} />}
          {record.label4 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label4")} text={record.label4} />}
          {record.label5 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label5")} text={record.label5} />}
        </Flex>
      )
    },
    {
      title: t("warehouse.locationIndex"),
      key: "l1",
      width: 220,
      render: (_, record) => (
        <SText text={getLocationIdx(record)} />
      )
    },
    {
      title: t("inventory.locationType"),
      key: "type",
      width: 140,
      render: (_, record) => {
        let info = locationTypes.find(label => label.key === record.type);
        return (
          info && <SText text={info.desc} />
        )
      }
    },
    {
      title: t('counting.lockState'),
      dataIndex: 'lockState',
      key: 'lockState',
      width: 120,
      render: (lockState: LocationLockEnum) => {
        const isLocked = lockState === LocationLockEnum.LOCKED;
        return (
          <SText tagMode
            tagIcon={isLocked ? <LockOutlined /> : <UnlockOutlined />}
            color={isLocked ? 'red' : 'green'}
            text={isLocked ? t('counting.counting') : t('counting.notCounting')}
          />
        );
      },
    },
    {
      title: t("common.desc"),
      dataIndex: "desc",
      key: "desc",
      width: 200,
      render: (text) => <SText text={text || ''} />
    },
    {
      title: t("common.opt"),
      fixed: "right",
      width: 330,
      render: (_, item) => (
        <>
          <Button type="link" onClick={() => setLDetail(item)}>
            {t("common.detail")}
          </Button>
          <Button type="link" onClick={() => {
            setEditingLocation(item);
            setLocationFormOpen(true);
          }}>
            {t("common.edit")}
          </Button>
          <Button type="link" onClick={() => {
            setSelectedLocationId(item.id);
          }}>
            {t("warehouse.productRef")}
          </Button>
          <Button type="link" onClick={() => setBarcodeInfo({ id: item.id, type: 'location' })}>
            {t("warehouse.viewBarcode")}
          </Button>
        </>
      )
    },
  ]

  const fetch = (offset: number, filters: LocationFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    // ✅ 取消上一次还在进行中的请求，避免旧响应覆盖新响应
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let params: CommonListParams = {
      pagination: { offset, take },
      body: filters.keyword,
      controller, // ✅ 传递 controller 用于取消请求（含 useDataList 的 Esc 中止）
    }
    getLList({ ...params }, filters.locationType)
  }

  const handleSearch = () => {
    const nextFilters = commitFilters();
    setSelectedRowKeys([]);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  }

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_LOCATION_FILTERS);
    setSelectedRowKeys([]);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  }

  // 库位导出列配置
  const locationExportColumns = [
    { key: "locationCode", label: t("warehouse.locationCode") },
    { key: "labels", label: t("warehouse.labels") },
    { key: "locationIndex", label: t("warehouse.locationIndex") },
    { key: "desc", label: t("common.desc") },
    { key: "lockState", label: t('counting.lockState') },
  ];

  const locationDefaultSelectedColumns = [
    t("warehouse.locationCode"),
    t("warehouse.labels"),
    t("warehouse.locationIndex"),
    t("common.desc"),
    t('counting.lockState')
  ];

  const locationExportDataMapper = (item: WLocation, t: any) => {
    const labels = [item.label1, item.label2, item.label3, item.label4, item.label5]
      .filter(Boolean)
      .join(', ');
    return {
      [t("warehouse.locationCode")]: getLocationCode(item),
      [t("warehouse.labels")]: labels,
      [t("warehouse.locationIndex")]: getLocationIdx(item),
      [t("common.desc")]: item.desc ?? '',
      [t('counting.lockState')]: item.lockState === LocationLockEnum.LOCKED ? t('counting.counting') : t('counting.notCounting'),
    };
  };

  // 获取选中的库位数据
  const getSelectedLocations = (): WLocation[] => {
    return lList.filter(item => selectedRowKeys.includes(item.id));
  };

  // 导出全部数据加载函数
  const handleLoadAllLocation = async () => {
    setExportProgressVisible(true);
    setExportProgress(0);
    const allData: WLocation[] = [];
    const batchSize = 100;
    const totalBatches = Math.ceil(lTotal / batchSize);

    try {
      for (let i = 0; i < totalBatches; i++) {
        const params: CommonListParams = {
          pagination: { offset: i * batchSize, take: batchSize },
          body: appliedFilters.keyword,
        };
        const response = await locationList(params, appliedFilters.locationType);
        allData.push(...(response.body ?? []));
        setExportProgress(Math.round(((i + 1) / totalBatches) * 100));
      }
      return allData;
    } finally {
      setExportProgressVisible(false);
      setExportProgress(0);
    }
  };

  const headerChildren = (
    <Flex justify='space-between' style={{ width: "100%" }} align='center'>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={lLoading}
        >
          {({ submit }) => (
            <>
              <SelectLocationType
                style={{ minWidth: 200 }}
                placeholder={t('inventory.locationType')}
                value={locationType}
                onChange={value => updateDraftFilters({ locationType: value })}
                allowClear
              />
              <Input style={{ width: 180 }}
                placeholder={t('warehouse.searchLocationPlaceholder')}
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                onPressEnter={submit}
              />
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        headerChildren={headerChildren}
        headerSub={<Flex style={{ marginTop: -10, marginBottom: 10 }} gap={10}>
          {header}
          <Button
            type="primary"
            onClick={() => {
              setEditingLocation(undefined);
              setLocationFormOpen(true);
            }}
          >
            {t('warehouse.addLocation')}
          </Button>
          <ExportExcelButton<WLocation>
            list={lList}
            columns={locationExportColumns}
            defaultSelectedColumns={locationDefaultSelectedColumns}
            dataMapper={locationExportDataMapper}
            fileName={t('warehouse.locationList') + '.xlsx'}
            sheetName={t('warehouse.locationList')}
          />
          <ExportExcelButton<WLocation>
            list={lList}
            isAll
            loadData={handleLoadAllLocation}
            columns={locationExportColumns}
            defaultSelectedColumns={locationDefaultSelectedColumns}
            dataMapper={locationExportDataMapper}
            fileName={t('warehouse.locationList') + '_全部.xlsx'}
            sheetName={t('warehouse.locationList')}
          />
          <PrintLocation
            datas={getSelectedLocations()}
            onStart={() => setPrintLoading(true)}
            onEnd={() => setPrintLoading(false)}
            disabled={selectedRowKeys.length === 0 || printLoading}
          />
        </Flex>}
        tableLayoutRef={tableRef}
        total={lTotal}
        handleChange={(offset) => { fetch(offset, appliedFilters) }}
        columns={LocationColums as ColumnsType<AnyObject>}
        dataSource={lList}
        loading={lLoading}
        scrollX={1000}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
      />
      {lDetail && <LocationDetail location={lDetail} onClose={() => setLDetail(null)} />}
      {barcodeInfo && <BarcodeList id={barcodeInfo.id} type={barcodeInfo.type} onClose={() => setBarcodeInfo(null)} />}
      <LocationForm
        detail={editingLocation}
        open={locationFormOpen}
        onClose={() => {
          setLocationFormOpen(false);
          setEditingLocation(undefined);
        }}
        onDone={(location) => {
          if (editingLocation) {
            updateLItem(location)
          } else {
            addLItem(location)
          }
          setLocationFormOpen(false);
          setEditingLocation(undefined);
        }}
      />
      <ExportProgressModal
        visible={exportProgressVisible}
        progress={exportProgress}
      />
      {selectedLocationId && (
        <LocationRefProducts
          locationId={selectedLocationId}
          open={true}
          onClose={() => {
            setSelectedLocationId('');
          }}
        />
      )}
    </Flex>
  );
};

export default LocationManage;
